import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
} from "@mui/material";
import api from "../api/axios";

type InfoResp =
  | { estado: "EXPIRED" | "SIGNED" | "REVOKED" }
  | {
      estado: "PENDING";
      reservaId: number;
      rol: "profesor" | "alumno";
      modo: "presencial" | "remoto";
      tipo: "SIGN" | "FORM";
    };

// ——— Canvas de firma sin dependencias ———
function SignaturePad(props: {
  onReady?: (api: { clear: () => void; toBase64: () => string; isEmpty: () => boolean }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [empty, setEmpty] = useState(true);

  // redraw helpers
  const fit = useCallback(() => {
    const c = canvasRef.current!;
    const parent = c.parentElement!;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const w = parent.clientWidth;
    const h = 240; // alto fijo
    c.width = Math.floor(w * dpr);
    c.height = Math.floor(h * dpr);
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    const ctx = c.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
  }, []);

  useEffect(() => {
    fit();
    const onResize = () => fit();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fit]);

  useEffect(() => {
    const c = canvasRef.current!;
    const ctx = c.getContext("2d")!;
    let drawing = false;
    let lastX = 0,
      lastY = 0;

    const pos = (e: PointerEvent) => {
      const rect = c.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const down = (e: PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      drawing = true;
      const p = pos(e);
      lastX = p.x;
      lastY = p.y;
      setEmpty(false);
    };

    const move = (e: PointerEvent) => {
      if (!drawing) return;
      e.preventDefault();
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastX = p.x;
      lastY = p.y;
    };

    const up = (e: PointerEvent) => {
      drawing = false;
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    };

    c.addEventListener("pointerdown", down);
    c.addEventListener("pointermove", move);
    c.addEventListener("pointerup", up);
    c.addEventListener("pointerleave", up);

    return () => {
      c.removeEventListener("pointerdown", down);
      c.removeEventListener("pointermove", move);
      c.removeEventListener("pointerup", up);
      c.removeEventListener("pointerleave", up);
    };
  }, []);

  // API pública del pad
  const apiObj = useMemo(
    () => ({
      clear: () => {
        const c = canvasRef.current!;
        const ctx = c.getContext("2d")!;
        const { width, height } = c;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.restore();
        // repinta fondo blanco
        const cssW = parseInt(c.style.width || "0", 10) || width;
        const cssH = parseInt(c.style.height || "0", 10) || height;
        const dpr = Math.max(window.devicePixelRatio || 1, 1);
        ctx.scale(dpr, dpr);
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, cssW, cssH);
        setEmpty(true);
      },
      toBase64: () => {
        const c = canvasRef.current!;
        const dataUrl = c.toDataURL("image/png");
        return dataUrl.replace(/^data:image\/png;base64,/, "");
      },
      isEmpty: () => empty,
    }),
    [empty]
  );

  useEffect(() => {
    props.onReady?.(apiObj);
  }, [apiObj, props]);

  return (
    <Box
      sx={{
        border: "1px dashed #bbb",
        borderRadius: 1,
        overflow: "hidden",
        touchAction: "none",
        bgcolor: "#fff",
      }}
    >
      <canvas ref={canvasRef} />
    </Box>
  );
}

export default function FirmarPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<InfoResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sigRef = useRef<{
    clear: () => void;
    toBase64: () => string;
    isEmpty: () => boolean;
  } | null>(null);
  const [sending, setSending] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // formulario motivo
  const [motivo, setMotivo] = useState("");

  // carga del estado del token
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!token) {
        setError("Falta el token en la URL");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get<InfoResp>("/firmar", {
          params: { token },
        });
        if (mounted) setInfo(data);
      } catch (e: any) {
        if (mounted) setError(e?.response?.data?.message || "No se pudo cargar el enlace");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const pending = info?.estado === "PENDING";
  const isForm = pending && (info as any)?.tipo === "FORM";
  const isSign = pending && ((info as any)?.tipo === "SIGN" || (info as any)?.tipo == null);

  const titulo = pending
    ? isForm
      ? "Formulario de no firma"
      : "Firma de asistencia"
    : info?.estado === "SIGNED"
      ? "Enlace ya utilizado"
      : info?.estado === "EXPIRED"
        ? "Enlace caducado"
        : info?.estado === "REVOKED"
          ? "Enlace revocado"
          : "Validando…";

  const onFirmar = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError("Debes trazar tu firma en el recuadro.");
      return;
    }
    setError(null);
    setSending(true);
    try {
      const firmaBase64 = sigRef.current.toBase64();
      await api.post("/firmar", { firmaBase64 }, { params: { token } });
      setOkMsg("¡Gracias! La firma se ha registrado correctamente.");
      setInfo({ estado: "SIGNED" });
    } catch (e: any) {
      setError(e?.response?.data?.message || "No se pudo enviar la firma");
    } finally {
      setSending(false);
    }
  };

  const onEnviarMotivo = async () => {
    const m = motivo.trim();
    if (!m) {
      setError("Indica el motivo");
      return;
    }
    if (m.length > 500) {
      setError("Máx. 500 caracteres");
      return;
    }
    setError(null);
    setSending(true);
    try {
      await api.post("/firmar", { motivo: m }, { params: { token } });
      setOkMsg("¡Gracias! Tu respuesta ha sido registrada.");
      setInfo({ estado: "SIGNED" });
    } catch (e: any) {
      setError(e?.response?.data?.message || "No se pudo enviar el formulario");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper sx={{ width: "100%", maxWidth: 720, p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h5">{titulo}</Typography>

          {loading && <CircularProgress />}

          {error && <Alert severity="error">{error}</Alert>}
          {okMsg && <Alert severity="success">{okMsg}</Alert>}

          {/* Estado del enlace */}
          {!loading && info && info.estado !== "PENDING" && (
            <>
              {info.estado === "SIGNED" && <Typography>Este enlace ya fue utilizado.</Typography>}
              {info.estado === "EXPIRED" && (
                <Typography>El enlace ha caducado. Solicita uno nuevo a tu centro.</Typography>
              )}
              {info.estado === "REVOKED" && <Typography>El enlace ha sido revocado.</Typography>}
            </>
          )}

          {/* FORM (motivo) cuando está PENDING */}
          {!loading && isForm && (
            <>
              <Alert severity="info">
                Indica el motivo por el que no se ha podido firmar (máx. 500 caracteres).
              </Alert>

              <Divider />

              <TextField
                fullWidth
                multiline
                rows={5}
                label="Motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value.slice(0, 500))}
                inputProps={{ maxLength: 500 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={onEnviarMotivo}
                  disabled={sending || !motivo.trim()}
                >
                  {sending ? "Enviando…" : "Enviar"}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Tu respuesta quedará registrada junto a la reserva.
              </Typography>
            </>
          )}

          {/* Firma normal cuando está PENDING */}
          {!loading && isSign && (
            <>
              <Alert severity="info">
                {(info as any)?.modo === "presencial" ? "Modo presencial" : "Modo remoto"} —{" "}
                {(info as any)?.rol === "alumno" ? "Firma del/a alumno/a" : "Firma del/a profesor/a"}
              </Alert>

              <Divider />

              <Typography variant="subtitle1">Dibuja tu firma en el recuadro:</Typography>
              <SignaturePad
                onReady={(api) => {
                  sigRef.current = api;
                }}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => sigRef.current?.clear()}>
                  Borrar
                </Button>
                <Button variant="contained" onClick={onFirmar} disabled={sending}>
                  {sending ? "Enviando…" : "Firmar"}
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Al pulsar “Firmar”, autorizas el tratamiento de tu firma para acreditar la
                asistencia a la clase.
              </Typography>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
